import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { IBxpWebStargateClient } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import {
	FastshiftRunbookStepStatus,
	OrgQueryParam,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

import { Org, PageStateCache, PortalUserConsent, TableId } from './types';

export const Actions = {
	GET_USER_DETAILS: '[FASTSHIFT] GET_USER_DETAILS',
	GET_USER_ACCESS: '[FASTSHIFT] GET_USER_ACCESS',
	GET_CUSTOMER_DOMAINS: '[FASTSHIFT] GET_CUSTOMER_DOMAINS',
	SET_SELECTED_ORG: '[FASTSHIFT] SET_SELECTED_ORG',
	GET_MIGRATION_PLANS: '[FASTSHIFT] GET_MIGRATION_PLANS',
	GET_CUSTOMER_DOMAIN: '[FASTSHIFT] GET_CUSTOMER_DOMAIN',
	GET_CUSTOMER_DOMAIN_ENGAGEMENT_DETAILS: '[FASTSHIFT] GET_CUSTOMER_DOMAIN_ENGAGEMENT_DETAILS',
	CLEAR_ALL_CUSTOMER_DOMAIN_SCOPED_STATE: '[FASTSHIFT] CLEAR_ALL_CUSTOMER_DOMAIN_SCOPED_STATE',
	CLEAR_CUSTOMER_DOMAIN: '[FASTSHIFT] CLEAR_CUSTOMER_DOMAIN',
	GET_USER_TYPE: '[FASTSHIFT] GET_USER_TYPE',
	GET_FASTSHIFT_ORGANIZATIONS: '[FASTSHIFT] GET_FASTSHIFT_ORGANIZATIONS',
	GET_FASTSHIFT_ORGANIZATION_SUMMARY: '[FASTSHIFT] GET_FASTSHIFT_ORGANIZATION_SUMMARY',
	GET_FASTSHIFT_ORGANIZATION_DC_INSTANCES: '[FASTSHIFT] GET_FASTSHIFT_ORGANIZATION_DC_INSTANCES',
	GET_FASTSHIFT_DC_INSTANCE_DETAILS: '[FASTSHIFT] GET_FASTSHIFT_DC_INSTANCE_DETAILS',
	GET_FASTSHIFT_ASSESSMENT_RECOMMENDATION_DETAILS:
		'[FASTSHIFT] GET_FASTSHIFT_ASSESSMENT_RECOMMENDATION_DETAILS',
	GET_FASTSHIFT_SUPPORT_REQUESTS: '[FASTSHIFT] GET_FASTSHIFT_SUPPORT_REQUESTS',
	SET_TABLE_CONTROLS: '[FASTSHIFT] SET_TABLE_CONTROLS',
	CLEAR_TABLE_CONTROLS: '[FASTSHIFT] CLEAR_TABLE_CONTROLS',
	GET_FASTSHIFT_TOP_SUPPORT_REQUESTS: '[FASTSHIFT] GET_FASTSHIFT_TOP_SUPPORT_REQUESTS',
	SET_PORTAL_USER_CONSENT: '[FASTSHIFT] SET_PORTAL_USER_CONSENT',
	SUBMIT_PORTAL_USER_CONSENT: '[FASTSHIFT] SUBMIT_PORTAL_USER_CONSENT',
	GET_PORTAL_USER_CONSENT: '[FASTSHIFT] GET_PORTAL_USER_CONSENT',
	GET_FASTSHIFT_RUNBOOKS_BY_SUPPORT_TICKET_ID:
		'[FASTSHIFT] GET_FASTSHIFT_RUNBOOKS_BY_SUPPORT_TICKET_ID',
	GET_FASTSHIFT_RUNBOOK: '[FASTSHIFT] GET_FASTSHIFT_RUNBOOK',
	UPDATE_FASTSHIFT_RUNBOOK_STEP_STATUS: '[FASTSHIFT] UPDATE_FASTSHIFT_RUNBOOK_STEP_STATUS',
	UPDATE_FASTSHIFT_RUNBOOK_STEP_RISK: '[FASTSHIFT] UPDATE_FASTSHIFT_RUNBOOK_STEP_RISK',
	UPDATE_FASTSHIFT_RUNBOOK_STEP_DISABLED: '[FASTSHIFT] UPDATE_FASTSHIFT_RUNBOOK_STEP_DISABLED',
	GET_FASTSHIFT_CS_PROFILER_PROFILING_OPTIONS:
		'[FASTSHIFT] GET_FASTSHIFT_CS_PROFILER_PROFILING_OPTIONS',
	GET_FASTSHIFT_CS_PROFILER_CUSTOMER_PROFILE:
		'[FASTSHIFT] GET_FASTSHIFT_CS_PROFILER_CUSTOMER_PROFILE',
	GET_FASTSHIFT_CS_PROFILER_LINKED_TICKETS: '[FASTSHIFT] GET_FASTSHIFT_CS_PROFILER_LINKED_TICKETS',
} as const;

export const setSelectedOrg = createAction<Org>(Actions.SET_SELECTED_ORG);

export const getCustomerDomains = createAsyncThunk(
	Actions.GET_CUSTOMER_DOMAINS,
	async ({
		stargateClient,
		controls,
	}: {
		stargateClient: IBxpWebStargateClient;
		controls: {
			page: number;
			size: number;
			sortBy?: string;
			sortDirection?: 'ASC' | 'DESC';
			searchTerm?: string;
			filters: Record<string, string | null>;
		};
	}) => {
		const response = await stargateClient.getFastshiftCustomerDomains({
			page: controls.page,
			size: controls.size,
			sortBy: controls.sortBy,
			sortDirection: controls.sortDirection,
			searchTerm: controls.searchTerm,
			filters: controls.filters,
		});
		return response;
	},
);

export const getCustomerDomain = createAsyncThunk(
	Actions.GET_CUSTOMER_DOMAIN,
	async ({
		stargateClient,
		customerDomainId,
	}: {
		stargateClient: IBxpWebStargateClient;
		customerDomainId: string;
	}) => {
		const response = await stargateClient.getFastshiftCustomerDomain({
			customerDomainId,
		});
		return response.data;
	},
);

export const getCustomerDomainEngagementDetails = createAsyncThunk(
	Actions.GET_CUSTOMER_DOMAIN_ENGAGEMENT_DETAILS,
	async ({
		stargateClient,
		customerDomainId,
	}: {
		stargateClient: IBxpWebStargateClient;
		customerDomainId: string;
	}) => {
		const response = await stargateClient.getFastshiftCustomerDomainEngagement({
			customerDomainId,
		});
		return response.data;
	},
);

export const clearAllCustomerDomainScopedState = createAction(
	Actions.CLEAR_ALL_CUSTOMER_DOMAIN_SCOPED_STATE,
);

export const clearCustomerDomain = createAction(Actions.CLEAR_CUSTOMER_DOMAIN);

export const getUserAccess = createAsyncThunk(
	Actions.GET_USER_ACCESS,
	async (stargateClient: IBxpWebStargateClient) => {
		const response = await stargateClient.getFastshiftUserAccess({});
		return response.data;
	},
);

export const getFastshiftTopSupportRequests = createAsyncThunk(
	Actions.GET_FASTSHIFT_TOP_SUPPORT_REQUESTS,
	async ({
		stargateClient,
		domainId,
		count,
		queryParams,
	}: {
		stargateClient: IBxpWebStargateClient;
		domainId: string;
		count: number;
		queryParams: OrgQueryParam;
	}) => {
		const response = await stargateClient.getFastshiftTopSupportRequests({
			domainId,
			count,
			queryParams,
		});
		return response.data;
	},
);

export const getFastshiftOrganizations = createAsyncThunk(
	Actions.GET_FASTSHIFT_ORGANIZATIONS,
	async ({
		stargateClient,
		domainId,
	}: {
		stargateClient: IBxpWebStargateClient;
		domainId: string;
	}) => {
		const response = await stargateClient.getFastshiftOrganizations({ domainId });

		return response;
	},
);

export const getFastshiftOrganizationSummary = createAsyncThunk(
	Actions.GET_FASTSHIFT_ORGANIZATION_SUMMARY,
	async ({
		stargateClient,
		domainId,
		orgId,
	}: {
		stargateClient: IBxpWebStargateClient;
		domainId: string;
		orgId: string;
	}) => {
		const response = await stargateClient.getFastshiftOrganizationSummary({ domainId, orgId });

		return response;
	},
);

export const getFastshiftOrganizationDcInstances = createAsyncThunk(
	Actions.GET_FASTSHIFT_ORGANIZATION_DC_INSTANCES,
	async ({
		stargateClient,
		domainId,
		orgId,
	}: {
		stargateClient: IBxpWebStargateClient;
		domainId: string;
		orgId: string;
	}) => {
		return stargateClient.getFastshiftOrganizationDcInstances({ domainId, orgId });
	},
);

export const getFastshiftDcInstanceDetails = createAsyncThunk(
	Actions.GET_FASTSHIFT_DC_INSTANCE_DETAILS,
	async ({
		stargateClient,
		domainId,
		orgId,
		instanceId,
	}: {
		stargateClient: IBxpWebStargateClient;
		domainId: string;
		orgId: string;
		instanceId: string;
	}) => {
		return stargateClient.getFastshiftDcInstanceDetails({ domainId, orgId, instanceId });
	},
);

export const getFastshiftAssessmentRecommendationDetails = createAsyncThunk(
	Actions.GET_FASTSHIFT_ASSESSMENT_RECOMMENDATION_DETAILS,
	async ({
		stargateClient,
		domainId,
		orgId,
		instanceId,
		assessmentId,
		recommendationId,
	}: {
		stargateClient: IBxpWebStargateClient;
		domainId: string;
		orgId: string;
		instanceId: string;
		assessmentId: string;
		recommendationId: string;
	}) => {
		return stargateClient.getFastshiftAssessmentRecommendationDetails({
			domainId,
			orgId,
			instanceId,
			assessmentId,
			recommendationId,
		});
	},
);

export const getFastshiftSupportRequests = createAsyncThunk(
	Actions.GET_FASTSHIFT_SUPPORT_REQUESTS,
	async ({
		stargateClient,
		customerDomainId,
		queryParams,
	}: {
		stargateClient: IBxpWebStargateClient;
		customerDomainId: string;
		queryParams: OrgQueryParam;
	}) => {
		const response = await stargateClient.getFastshiftSupportRequests({
			customerDomainId,
			queryParams,
		});

		return response;
	},
);

export const getPortalUserConsent = createAsyncThunk(
	Actions.GET_PORTAL_USER_CONSENT,
	async ({
		stargateClient,
		displayedText,
	}: {
		stargateClient: IBxpWebStargateClient;
		displayedText: string;
	}) => {
		const response = await stargateClient.fetchFastshiftPortalConsentRequest({
			requestBody: {
				displayedText,
			},
		});
		return response.data.consent;
	},
);

export const submitPortalUserConsent = createAsyncThunk(
	Actions.SUBMIT_PORTAL_USER_CONSENT,
	async ({
		stargateClient,
		displayedText,
		locale,
	}: {
		stargateClient: IBxpWebStargateClient;
		displayedText: string;
		locale: string;
	}) => {
		const response = await stargateClient.sendFastshiftPortalConsent({
			requestBody: {
				displayedText,
				locale,
			},
		});
		return response.data.consent;
	},
);

export const setPortalUserConsent = createAction<PortalUserConsent['data']>(
	Actions.SET_PORTAL_USER_CONSENT,
);

export const setTableControls = createAction<{
	tableId: TableId;
	controls: PageStateCache[TableId];
}>(Actions.SET_TABLE_CONTROLS);

export const clearTableControls = createAction<{
	tableId: TableId;
}>(Actions.CLEAR_TABLE_CONTROLS);

export const getFastshiftRunbooksBySupportTicketId = createAsyncThunk(
	Actions.GET_FASTSHIFT_RUNBOOKS_BY_SUPPORT_TICKET_ID,
	async ({
		stargateClient,
		supportTicketId,
	}: {
		stargateClient: IBxpWebStargateClient;
		supportTicketId: string;
	}) => {
		const response = await stargateClient.getFastshiftRunbooksBySupportTicketId({
			supportTicketId,
		});

		return response.data;
	},
);

export const getFastshiftRunbook = createAsyncThunk(
	Actions.GET_FASTSHIFT_RUNBOOK,
	async ({
		stargateClient,
		supportTicketId,
		runbookId,
	}: {
		stargateClient: IBxpWebStargateClient;
		supportTicketId: string;
		runbookId: string;
	}) => {
		const response = await stargateClient.getFastshiftRunbook({
			supportTicketId,
			runbookId,
		});

		return response.data;
	},
);

export const updateFastshiftRunbookStepStatus = createAsyncThunk(
	Actions.UPDATE_FASTSHIFT_RUNBOOK_STEP_STATUS,
	async ({
		stargateClient,
		supportTicketId,
		runbookId,
		stepId,
		status,
		note,
		title,
		phase,
		responsible,
	}: {
		stargateClient: IBxpWebStargateClient;
		supportTicketId: string;
		runbookId: string;
		stepId: string;
		status: FastshiftRunbookStepStatus;
		note?: string;
		title: string;
		phase: string;
		responsible: string;
	}) => {
		const response = await stargateClient.updateFastshiftRunbookStepStatus({
			supportTicketId,
			runbookId,
			stepId,
			status,
			note,
			title,
			phase,
			responsible,
		});

		return response;
	},
);

export const updateFastshiftRunbookStepRisk = createAsyncThunk(
	Actions.UPDATE_FASTSHIFT_RUNBOOK_STEP_RISK,
	async ({
		stargateClient,
		supportTicketId,
		runbookId,
		stepId,
		risk,
	}: {
		stargateClient: IBxpWebStargateClient;
		supportTicketId: string;
		runbookId: string;
		stepId: string;
		risk: boolean;
	}) => {
		const response = await stargateClient.updateFastshiftRunbookStepRisk({
			supportTicketId,
			runbookId,
			stepId,
			risk,
		});

		return response;
	},
);

export const updateFastshiftRunbookStepDisabled = createAsyncThunk(
	Actions.UPDATE_FASTSHIFT_RUNBOOK_STEP_DISABLED,
	async ({
		stargateClient,
		supportTicketId,
		runbookId,
		stepId,
		disabled,
	}: {
		stargateClient: IBxpWebStargateClient;
		supportTicketId: string;
		runbookId: string;
		stepId: string;
		disabled: boolean;
	}) => {
		const response = await stargateClient.updateFastshiftRunbookStepDisabled({
			supportTicketId,
			runbookId,
			stepId,
			disabled,
		});

		return response;
	},
);

export const getFastshiftCsProfilerProfilingOptions = createAsyncThunk(
	Actions.GET_FASTSHIFT_CS_PROFILER_PROFILING_OPTIONS,
	async ({ stargateClient }: { stargateClient: IBxpWebStargateClient }) => {
		const response = await stargateClient.getFastshiftCsProfilerProfilingOptions({});

		if (!response.success || !response.data) {
			throw new Error('Failed to fetch CS profiler profiling options');
		}

		return response.data;
	},
);

export const getFastshiftCsProfilerCustomerProfile = createAsyncThunk(
	Actions.GET_FASTSHIFT_CS_PROFILER_CUSTOMER_PROFILE,
	async ({
		stargateClient,
		region,
		industry,
		segment,
	}: {
		stargateClient: IBxpWebStargateClient;
		region: string;
		industry: string;
		segment: string;
	}) => {
		const response = await stargateClient.getFastshiftCsProfilerCustomerProfile({
			region,
			industry,
			segment,
		});

		if (!response.success || !response.data) {
			throw new Error('Failed to fetch CS profiler customer profile');
		}

		return response.data;
	},
);

export const getFastshiftCsProfilerLinkedTickets = createAsyncThunk(
	Actions.GET_FASTSHIFT_CS_PROFILER_LINKED_TICKETS,
	async ({ stargateClient, theme }: { stargateClient: IBxpWebStargateClient; theme: string }) => {
		const response = await stargateClient.getFastshiftCsProfilerLinkedTickets({ theme });

		if (!response.success || !response.data) {
			throw new Error('Failed to fetch CS profiler linked tickets');
		}

		return response.data;
	},
);

export type Actions = (typeof Actions)[keyof typeof Actions];
